// Die Seite hinter dem Link aus der Mail „Passwort zurücksetzen".
//
// Das Öffnen verbraucht den Link nicht — Mailprogramme rufen Links vorab auf.
// Erst das Absenden des neuen Passworts löst ihn ein. Der Link verschwindet
// gleich aus der Adresszeile, damit er nicht im Verlauf oder in einem
// Bildschirmfoto landet.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Feld, Hinweis, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';
import AuthRahmen from '../komponenten/AuthRahmen';

type Stand = 'pruefe' | 'eingabe' | 'fertig' | 'abgelaufen' | 'ungueltig';

/** Dieselben Regeln wie im Backend. */
const regelnErfuellt = (pw: string) => pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);

export default function PasswortNeu() {
  const { t } = useSprache();
  // Einmal beim Laden lesen; danach steht der Link nicht mehr in der Adresszeile.
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') ?? '');
  const [stand, setStand] = useState<Stand>(token ? 'pruefe' : 'ungueltig');
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const schonGeprueft = useRef(false);

  useEffect(() => {
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
    if (!token || schonGeprueft.current) return;
    schonGeprueft.current = true;
    api<{ benutzername: string }>('/auth/passwort-zuruecksetzen/pruefen', { methode: 'POST', daten: { token } })
      .then(r => { setName(r.benutzername); setStand('eingabe'); })
      .catch(e => setStand(e instanceof ApiFehler && e.code === 'ABGELAUFEN' ? 'abgelaufen' : 'ungueltig'));
  }, [token]);

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (pw !== pw2) { setFehler(t('passwort.ungleich')); return; }
    if (!regelnErfuellt(pw)) { setFehler(t('passwort.regeln')); return; }
    setLaedt(true);
    try {
      await api('/auth/passwort-zuruecksetzen', { methode: 'POST', daten: { token, passwort: pw } });
      setStand('fertig');
    } catch (e) {
      if (e instanceof ApiFehler && (e.code === 'ABGELAUFEN' || e.code === 'UNGUELTIG')) {
        setStand(e.code === 'ABGELAUFEN' ? 'abgelaufen' : 'ungueltig');
      } else {
        setFehler(e instanceof ApiFehler ? e.message : t('anmelden.fehler'));
      }
    } finally { setLaedt(false); }
  }

  const titel = (text: string) => <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">{text}</h1>;
  const neuAnfordern = (
    <Link to="/passwort-vergessen"><Knopf variante="primaer" className="w-full">{t('passwort.neuAnfordern')}</Knopf></Link>
  );

  return (
    <AuthRahmen>
      {stand === 'pruefe' && (
        <div className="space-y-3 text-center"><Spinner /><p className="text-sm text-slate-600 dark:text-slate-300">{t('passwort.pruefe')}</p></div>
      )}

      {stand === 'eingabe' && (
        <form onSubmit={speichern} className="space-y-4">
          <div>
            {titel(t('passwort.neuTitel'))}
            {name && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('passwort.fuer').replace('{name}', name)}</p>}
          </div>
          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
          {/* Unsichtbarer Benutzername: Passwort-Manager ordnen das neue Passwort so dem richtigen Konto zu. */}
          <input type="text" name="username" value={name} autoComplete="username" readOnly hidden />
          <Feld label={t('passwort.neu')} type="password" value={pw} onChange={e => setPw(e.target.value)}
                autoComplete="new-password" autoFocus required hinweis={t('passwort.regeln')} />
          <Feld label={t('passwort.wiederholen')} type="password" value={pw2} onChange={e => setPw2(e.target.value)}
                autoComplete="new-password" required />
          <Knopf type="submit" variante="primaer" laedt={laedt} disabled={!pw || !pw2} className="w-full">
            {t('passwort.speichern')}
          </Knopf>
        </form>
      )}

      {stand === 'fertig' && (
        <>
          {titel(t('passwort.fertigTitel'))}
          <Hinweis art="erfolg">{t('passwort.fertigText')}</Hinweis>
          <Link to="/anmelden"><Knopf variante="primaer" className="w-full">{t('passwort.zurAnmeldung')}</Knopf></Link>
        </>
      )}

      {stand === 'abgelaufen' && (
        <>{titel(t('passwort.abgelaufenTitel'))}<Hinweis art="warnung">{t('passwort.abgelaufenText')}</Hinweis>{neuAnfordern}</>
      )}

      {stand === 'ungueltig' && (
        <>{titel(t('passwort.ungueltigTitel'))}<Hinweis art="fehler">{t('passwort.ungueltigText')}</Hinweis>{neuAnfordern}</>
      )}
    </AuthRahmen>
  );
}
